import { isSoulMapMode } from '@/utils/app-mode';
import {
  getLoginRedirectPath,
  getSoulMapConsumerLandingPath,
  getSoulMapHomePath,
  isSoulMapLoginPage,
  shouldUseSoulMapConsumerLanding,
} from '@/utils/soulmap-auth';
import {
  extractTenantSlugFromPath,
  resolveHomeTenantSlug,
} from '@/utils/active-tenant';
import { HUB_TAB_PARAM } from '@/hooks/useHubTab';
import { getPostLoginReturnPath } from '@/utils/post-login-return-path';
import { useAuthStore } from '@/store/use-auth-store';
import { useLoaderStore } from '@/store/use-loader-store';
import { useUserStore } from '@/store/use-user-store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

interface NavigateOptions {
  replace?: boolean;
  state?: any;
}

interface NavigateToHomeOptions extends NavigateOptions {
  // Hub tab to select when home is the dashboard.
  hubTab?: string | null;
}

export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlTenantSlug = useParams<{ tenantSlug: string }>().tenantSlug;
  const { isAuthenticated, tenants, showTenantSelector } = useAuthStore();
  const { hasAdminPermissions } = useUserStore();
  const { isLoading } = useLoaderStore();

  const tenantSlug =
    urlTenantSlug ?? extractTenantSlugFromPath(location.pathname);

  const getTenantPath = (path: string) => {
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    return tenantSlug ? `/${tenantSlug}/${path}` : path;
  };

  const navigateToLogin = (options: NavigateOptions = {}) => {
    // Don't navigate while loading
    if (isLoading) return;

    const loginPath = getLoginRedirectPath(location.pathname);

    // Already on login: only re-navigate to apply the tenant selector state,
    // otherwise a signed-in multi-tenant user is left on the login form.
    const applyTenantSelector =
      options.state?.showTenantSelector &&
      !showTenantSelector &&
      (tenants?.length ?? 0) > 1;
    if (location.pathname === loginPath && !applyTenantSelector) {
      return;
    }

    navigate(loginPath, options);
  };

  const navigateToHome = ({
    hubTab,
    ...options
  }: NavigateToHomeOptions = {}) => {
    // Don't navigate while loading
    if (isLoading) return;

    console.log('navigateToHome', {
      isAuthenticated,
      tenants,
      tenantSlug,
      showTenantSelector,
      pathname: location.pathname,
    });

    // If not authenticated, go to login
    if (!isAuthenticated) {
      navigateToLogin(options);
      return;
    }

    if (showTenantSelector) {
      const loginPath = getLoginRedirectPath(location.pathname);
      if (location.pathname !== loginPath) {
        navigate(loginPath, {
          ...options,
          state: { showTenantSelector: true },
        });
      }
      return;
    }

    if (shouldUseSoulMapConsumerLanding()) {
      const landingPath = getSoulMapConsumerLandingPath();
      if (!isSoulMapLoginPage(location.pathname)) {
        navigate(landingPath, options);
      }
      return;
    }

    if (isSoulMapMode()) {
      const currentSlug = tenantSlug || tenants?.[0]?.slug || null;

      if (currentSlug) {
        const targetPath = getSoulMapHomePath(currentSlug);
        if (location.pathname !== targetPath) {
          navigate(targetPath, options);
        }
      }
      return;
    }

    // If no tenants data, go to login
    if (!tenants) {
      navigateToLogin(options);
      return;
    }

    // Just signed in (still outside a tenant, e.g. on /login) from a link
    // opened while signed out: go back to it. In-app calls are unaffected.
    const returnPath = tenantSlug ? null : getPostLoginReturnPath(tenants);
    if (returnPath) {
      if (`${location.pathname}${location.search}` !== returnPath) {
        navigate(returnPath, options);
      }
      return;
    }

    // Tenant from the URL, else the only / last-used one
    const currentSlug = tenantSlug || resolveHomeTenantSlug(tenants);

    // Multiple tenants and none to pick for the user: show the selector
    if (!currentSlug && tenants.length > 1 && !showTenantSelector) {
      navigateToLogin({ ...options, state: { showTenantSelector: true } });
      return;
    }

    if (currentSlug) {
      const targetPath = `/${currentSlug}/home`;

      // Only navigate if we're not already on Home
      if (location.pathname !== targetPath) {
        const search = hubTab
          ? `?${new URLSearchParams({ [HUB_TAB_PARAM]: hubTab })}`
          : '';
        navigate(`${targetPath}${search}`, options);
      }
      return;
    }

    // If we have no valid slug but have tenants, show selector
    navigateToLogin({ ...options, state: { showTenantSelector: true } });
  };

  const navigateToTenantHome = (options: NavigateOptions = {}) => {
    if (isLoading) return;

    if (!tenantSlug) {
      navigateToHome(options);
      return;
    }
    navigate(`/${tenantSlug}/home`, options);
  };

  const navigateToProtectedRoute = (
    path: string,
    options: NavigateOptions = {}
  ) => {
    if (isLoading) return;

    if (!hasAdminPermissions) {
      navigateToTenantHome(options);
      return;
    }
    navigate(getTenantPath(path), options);
  };

  const navigateToTenantRoute = (
    path: string,
    options: NavigateOptions = {}
  ) => {
    if (isLoading) return;

    if (!isAuthenticated) {
      navigateToLogin(options);
      return;
    }
    navigate(getTenantPath(path), options);
  };

  const navigateBack = () => {
    if (isLoading) return;
    navigate(-1);
  };

  const reload = () => {
    window.location.reload();
  };

  return {
    navigateToLogin,
    navigateToHome,
    navigateToTenantHome,
    navigateToProtectedRoute,
    navigateToTenantRoute,
    navigateBack,
    reload,
    currentPath: location.pathname,
    currentTenantSlug: tenantSlug,
  };
};
